import { isEmpty, toNumber } from 'lodash'

import { apiService, fetchingAPI } from '@/api'
import { toast } from '@/components/toasts/toast.helpers'
import {
  cleanBooleanValue,
  getTagsWithCategories,
  getDateRangeFromSavedFilter,
  getAgentOptions,
} from '@/utils/helpers'
import { fetchVoipCampaignsAPI } from '@/reducers/callSearch/callSearch.actions'
import { buildDashboardParams } from '@/views/QACopilot/CopilotDashboard/dashboard/helpers/dashboardParams'
import {
  formatQACopilotFilters,
  filterOutUnmappedCampaigns,
} from '@/reducers/qa-copilot/qa-copilot.helpers'
import { COPILOT_FILTER_TYPE } from '@/reducers/savedFilters/savedFilters.constants'
import { fetchSavedFilters } from '@/reducers/savedFilters/savedFilters.actions'
import { fetchPrompts } from '@/reducers/prompts/prompts.actions'
import { promptHasChangesHelper, withoutLegacyModel } from '@/reducers/prompts/prompts.helpers'
import { setPrompts, setPristinePrompts } from '@/reducers/prompts/prompts.redux'
import { recordAdoptionAfterSave } from '@/reducers/promptimizer/promptimizer.actions'
import {
  setData,
  setError,
  setFilter,
  clearFilters,
  setLoading,
  setScorecardConfig,
  setScorecardConfigVersions,
  setScorecardConfigChanges,
} from './qa-copilot.redux'

// Drop an option-fetch response that resolved after the user already
// switched orgs -- two orgs' fetches can be in flight at once (admin
// mount auto-default racing an org pick) and the loser used to
// overwrite the current org's scorecards/agents/tags with the old
// org's list.
const orgStillCurrent = (getState, organizationId) => {
  const current = getState().qaCopilot.filters?.organizationId
  return !current || String(current) === String(organizationId)
}

// Filters
export const fetchAgentsByOrg = (organizationId) => async (dispatch, getState) => {
  dispatch(setLoading({ agents: true }))

  try {
    const { users } = await fetchingAPI(
      `${apiService.core}/api/organizations/${organizationId}/users`,
      'GET',
      dispatch
    )

    if (!orgStillCurrent(getState, organizationId)) {
      return
    }
    dispatch(setData({ agents: getAgentOptions(users) }))
  } catch (err) {
    toast.error('Failed to fetch agents', { cause: err })
  } finally {
    dispatch(setLoading({ agents: false }))
  }
}

export const fetchPlaybooksByOrg = (organizationId) => async (dispatch, getState) => {
  dispatch(setLoading({ playbooks: true }))

  try {
    const playbooks = await fetchingAPI(
      `${apiService.core}/api/${organizationId}/configs?requested_properties=name,cid,organization_id,organization_name`,
      'GET',
      dispatch
    )

    const playbookOptions = playbooks
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((playbook) => ({
        value: playbook.cid,
        label: playbook.name,
      }))

    if (!orgStillCurrent(getState, organizationId)) {
      return
    }
    dispatch(setData({ playbooks: playbookOptions }))
  } catch (err) {
    toast.error('Failed to fetch playbooks', { cause: err })
  } finally {
    dispatch(setLoading({ playbooks: false }))
  }
}

export const fetchTagsByOrg = (organizationId) => async (dispatch, getState) => {
  dispatch(setLoading({ tags: true }))

  try {
    const [tags, tagCategories] = await Promise.all([
      fetchingAPI(`${apiService.core}/api/organizations/${organizationId}/tags`, 'GET', dispatch),
      fetchingAPI(
        `${apiService.core}/api/organizations/${organizationId}/tags/categories`,
        'GET',
        dispatch
      ),
    ])

    const tagsOptionsWithCategories = getTagsWithCategories(tags, tagCategories)

    if (!orgStillCurrent(getState, organizationId)) {
      return
    }
    dispatch(setData({ tags: tagsOptionsWithCategories }))
  } catch (err) {
    toast.error('Failed to fetch tags', { cause: err })
  } finally {
    dispatch(setLoading({ tags: false }))
  }
}

export const fetchDispositionsByOrg = (organizationId) => async (dispatch, getState) => {
  dispatch(setLoading({ dispositions: true }))

  try {
    const dispositions = await fetchingAPI(
      `${apiService.reporting}/api/dispositions?organization_id=${organizationId}`,
      'GET',
      dispatch
    )
    const dispoOptions = dispositions
      .map(({ name }) => ({ value: name, label: name }))
      .sort((a, b) => a.label.localeCompare(b.label))
    if (!orgStillCurrent(getState, organizationId)) {
      return
    }
    dispatch(setData({ dispositions: dispoOptions }))
  } catch (err) {
    toast.error('Failed to fetch dispositions', { cause: err })
  } finally {
    dispatch(setLoading({ dispositions: false }))
  }
}

export const fetchVoipCampaignsByOrg = (organizationId) => async (dispatch, getState) => {
  dispatch(setLoading({ voipCampaigns: true }))

  try {
    const { startDate, endDate } = getState().qaCopilot.filters

    const voipCampaignOptions = await fetchVoipCampaignsAPI(
      organizationId,
      startDate,
      endDate,
      dispatch
    )

    const filteredOptions = filterOutUnmappedCampaigns(voipCampaignOptions)

    const currentFilters = getState().qaCopilot.filters.voipCampaigns
    const newVoipCampaignFilters = currentFilters.filter((f) => {
      return filteredOptions.some((option) => option.value === f.value)
    })

    if (!orgStillCurrent(getState, organizationId)) {
      return
    }
    dispatch(setFilter({ voipCampaigns: newVoipCampaignFilters }))
    dispatch(setData({ voipCampaigns: filteredOptions }))
  } catch (err) {
    toast.error('Failed to fetch VOIP campaigns', { cause: err })
  } finally {
    dispatch(setLoading({ voipCampaigns: false }))
  }
}

export const fetchActiveVoipCampaignsByOrg = (organizationId) => async (dispatch) => {
  try {
    const sixtyDaysAgo = new Date()
    sixtyDaysAgo.setDate(sixtyDaysAgo.getDate() - 60)
    const today = new Date()

    const voipCampaignOptions = await fetchVoipCampaignsAPI(
      organizationId,
      sixtyDaysAgo,
      today,
      dispatch
    )

    const filteredOptions = filterOutUnmappedCampaigns(voipCampaignOptions)

    dispatch(setData({ activeVoipCampaigns: filteredOptions }))
  } catch (err) {
    dispatch(setData({ activeVoipCampaigns: [] }))
  }
}

export const fetchOrganizationsMappedVoipCampaigns = (organizationId) => async (dispatch) => {
  dispatch(setLoading({ allVoipCampaigns: true }))

  try {
    const response = await fetchingAPI(
      `${apiService.core}/api/organizations/${organizationId}/voip_campaigns?filter=mapped`,
      'GET',
      dispatch
    )
    dispatch(
      setData({
        allVoipCampaigns: response.mapped_campaigns || [],
      })
    )
  } catch (err) {
    toast.error('Failed to fetch VoIP campaigns', { cause: err })
  } finally {
    dispatch(setLoading({ allVoipCampaigns: false }))
  }
}

export const fetchCopilotScorecardsByOrg =
  (organizationId, isEnterprise, scorecardType = 'copilot') =>
  async (dispatch, getState) => {
    dispatch(setLoading({ scorecards: true }))

    try {
      const scorecards = await fetchingAPI(
        `${apiService.scorecard}/scoring/scorecards/current?requested_organization_id=${organizationId}&scorecard_type=${scorecardType}${isEnterprise ? '&hierarchy=True' : ''}`,
        'GET',
        dispatch
      )
      const scorecardOptions = scorecards
        .map((scorecard) => ({
          value: scorecard.sid,
          label: scorecard.name,
          // The configured "Below (%)" pass line -- the same cutoff the
          // Pass Rate KPI uses (threshold=improvement). Carried on the
          // option so the Threshold Sensitivity widget can mark it.
          improvement_threshold: scorecard.improvement_threshold,
        }))
        .sort((a, b) => a.label.localeCompare(b.label))

      if (!orgStillCurrent(getState, organizationId)) {
        return
      }
      dispatch(setData({ scorecards: scorecardOptions }))
    } catch (err) {
      toast.error('Failed to fetch scorecards', { cause: err })
    } finally {
      dispatch(setLoading({ scorecards: false }))
    }
  }

// The versions endpoint filters strictly on the caller's org, unlike the fetch-by-id above, which lets a Balto
// admin through on the scorecard's own org. Without the scorecard's org the list comes back empty for them — and
// for a hierarchy manager viewing a child org — so the version selector has nothing to offer.
export const fetchScorecardConfigVersions = (sid, organizationId) => async (dispatch) => {
  dispatch(setLoading({ scorecardConfigVersions: true }))
  const orgParam = organizationId ? `?requested_organization_id=${organizationId}` : ''

  try {
    const versions = await fetchingAPI(
      `${apiService.scorecard}/scorecard_config/versions/${sid}${orgParam}`,
      'GET',
      dispatch
    )
    dispatch(setScorecardConfigVersions(versions.sort((a, b) => b.id - a.id)))
  } catch (err) {
    toast.error('Failed to fetch scorecard versions', { cause: err })
  } finally {
    dispatch(setLoading({ scorecardConfigVersions: false }))
  }
}

export const fetchScorecardConfigChanges = (sid) => async (dispatch) => {
  dispatch(setLoading({ scorecardConfigChanges: true }))

  try {
    const changes = await fetchingAPI(
      `${apiService.scorecard}/history/scorecard_configs/${sid}`,
      'GET',
      dispatch
    )
    dispatch(setScorecardConfigChanges(changes))
  } catch (err) {
    toast.error('Failed to fetch scorecard change history', { cause: err })
  } finally {
    dispatch(setLoading({ scorecardConfigChanges: false }))
  }
}

// Scorecard CRUD
export const fetchScorecardConfigById = (scorecardConfigId, canEditPrompts) => async (dispatch) => {
  dispatch(setLoading({ scorecardConfig: true }))

  try {
    const scorecardConfig = await fetchingAPI(
      `${apiService.scorecard}/scoring/scorecards/scorecard/${scorecardConfigId}`,
      'GET',
      dispatch
    )
    dispatch(setScorecardConfig(scorecardConfig))
    if (canEditPrompts) {
      // Build a mapping of prompt_id to criteria scid so we can associate
      // fetched prompts with their criteria (prompts from server don't have scid)
      const allCriteria = scorecardConfig.sections
        .flatMap((section) => section.measures)
        .flatMap((measure) => measure.criteria)

      const promptIdToScid = {}
      allCriteria.forEach((criteria) => {
        if (criteria.prompt_id != null) {
          promptIdToScid[criteria.prompt_id] = criteria.scid
        }
      })

      const promptIds = Object.keys(promptIdToScid).map(Number)

      if (scorecardConfig.eligibility_prompt_id != null) {
        promptIds.push(scorecardConfig.eligibility_prompt_id)
        promptIdToScid[scorecardConfig.eligibility_prompt_id] = 'eligibility'
      }

      if (!isEmpty(promptIds)) {
        dispatch(fetchPrompts(promptIds, promptIdToScid))
      }
    }
    return scorecardConfig
  } catch (err) {
    toast.error('Failed to fetch scorecard', { cause: err })
    dispatch(setError({ scorecardConfig: true }))
    return null
  } finally {
    dispatch(setLoading({ scorecardConfig: false }))
  }
}

// A prompt with no `llm_model_id` has no model for the API to run on (it only falls back to the legacy `model`
// name, which the web app no longer sends), so it comes back as an unexplained 500. The criteria and settings forms
// require a model, but the bulk paths — CSV auto-populate, guided setup — build prompts without a form, so the save
// path guards too and names the problem.
const MISSING_PROMPT_MODEL = 'MissingPromptModelError'

const missingPromptModelError = () => {
  // Covers the eligibility prompt as well as criteria, so the wording is prompt-scoped.
  const error = new Error('Every prompt needs an AI model before it can be saved')
  error.name = MISSING_PROMPT_MODEL
  return error
}

/**
 * Create the prompts a scorecard save needs, and point the config's criteria at them.
 *
 * Only the prompts a user actually changed are sent: the API mints a new prompt version per POST, so sending the
 * untouched ones would version every criterion's prompt on an unrelated edit, such as renaming the scorecard or
 * moving a point value.
 *
 * Returns the config to write and the prompts the API now holds, which the caller stores once the write lands.
 */
const saveScorecardPrompts = async (scorecardConfig, prompts, pristinePrompts, dispatch) => {
  const withOrg = (prompt) => ({ ...prompt, organization_id: scorecardConfig.organization_id })
  const needsWrite = (prompt) =>
    Boolean(prompt.scid) && promptHasChangesHelper(withOrg(prompt), pristinePrompts)

  // The API has no model to run a prompt on without an llm_model_id, and answers an unexplained 500. The criteria
  // and settings forms require one, but the bulk paths — CSV auto-populate, guided setup — build prompts without a
  // form, so this is the backstop.
  //
  // Scoped to the prompts this save writes: an untouched one is never POSTed, so a legacy prompt whose model name
  // never resolved to an id must not block a save that leaves it alone. And checked up front rather than inside the
  // writes below, which run concurrently — throwing mid-flight would leave the prompts that already landed minted
  // with no config version pointing at them.
  if (prompts.filter(needsWrite).some((prompt) => !prompt.llm_model_id)) {
    throw missingPromptModelError()
  }

  const createWithScid = async (prompt) => {
    if (!needsWrite(prompt)) {
      return withOrg(prompt)
    }

    const body = JSON.stringify(withoutLegacyModel(withOrg(prompt)))
    const response = await fetchingAPI(`${apiService.summary}/prompts`, 'POST', dispatch, body)

    return {
      ...response,
      scid: prompt.scid,
      // The create response carries no tests. They hang off the prompt_uuid this new version inherits, so the
      // ones already in hand are still the lineage's.
      prompt_tests: prompt.prompt_tests || response.prompt_tests,
    }
  }

  // Link criteria to their new prompt ids by scid where we can; a prompt we skipped keeps the id it already had.
  const savedPrompts = await Promise.all(prompts.map((prompt) => createWithScid(prompt)))

  return {
    savedPrompts,
    scorecardConfigWithPrompts: {
      ...scorecardConfig,
      eligibility_prompt_id:
        savedPrompts.find((prompt) => prompt.scid === 'eligibility')?.id ||
        scorecardConfig.eligibility_prompt_id,
      sections: scorecardConfig.sections.map((section) => ({
        ...section,
        measures: section.measures.map((measure) => ({
          ...measure,
          criteria: measure.criteria.map((criteria) => ({
            ...criteria,
            prompt_id:
              savedPrompts.find((prompt) => prompt.scid === criteria.scid)?.id ||
              criteria.prompt_id,
          })),
        })),
      })),
    },
  }
}

/**
 * Store the prompts a save just wrote, so the reload it navigates into doesn't GET every one of them back.
 *
 * Only ever called once the config write landed: until then the criteria still point at the old prompt ids, and a
 * retry has to diff against the prompts the API had, not the ones a failed save left behind.
 */
const storeSavedPrompts = (savedPrompts, dispatch) => {
  if (isEmpty(savedPrompts)) {
    return
  }

  dispatch(setPrompts(savedPrompts))
  dispatch(setPristinePrompts(savedPrompts))
}

export const createScorecardConfig =
  (scorecardConfig, history, prompts = []) =>
  async (dispatch, getState) => {
    dispatch(setLoading({ scorecardConfig: true }))

    try {
      const { savedPrompts, scorecardConfigWithPrompts } = await saveScorecardPrompts(
        scorecardConfig,
        prompts,
        getState().prompts.pristinePrompts,
        dispatch
      )
      const newScorecardConfig = await fetchingAPI(
        `${apiService.scorecard}/scoring/scorecards/scorecard/`,
        'POST',
        dispatch,
        JSON.stringify(scorecardConfigWithPrompts)
      )
      storeSavedPrompts(savedPrompts, dispatch)
      const nextUrl = scorecardConfig.type === 'copilot' ? '/qa-copilot' : '/coaching/scorecards'
      history.push(`${nextUrl}/${newScorecardConfig.id}`)
      toast.success('Scorecard created!')
      return newScorecardConfig
    } catch (err) {
      toast.error(err?.name === MISSING_PROMPT_MODEL ? err.message : 'Failed to create scorecard')
      return null
    } finally {
      dispatch(setLoading({ scorecardConfig: false }))
    }
  }

export const updateScorecardConfig =
  (scorecardConfig, history, prompts, restoreVersion = false) =>
  async (dispatch, getState) => {
    dispatch(setLoading({ scorecardConfig: true }))

    try {
      const { savedPrompts, scorecardConfigWithPrompts } = await saveScorecardPrompts(
        scorecardConfig,
        prompts,
        getState().prompts.pristinePrompts,
        dispatch
      )
      const body = JSON.stringify(scorecardConfigWithPrompts)
      const updatedScorecardConfig = await fetchingAPI(
        `${apiService.scorecard}/scoring/scorecards/scorecard/${scorecardConfig.id}?restore_version=${restoreVersion}`,
        'PUT',
        dispatch,
        body
      )
      storeSavedPrompts(savedPrompts, dispatch)
      // If this save carried a prompt the optimizer suggested, that is the moment it became live — record it.
      // A no-op unless a candidate was applied, which is why this can sit on the shared save path without it
      // knowing anything about prompt optimization. The evidence the server wants is the prompt version this save
      // just minted in the criterion's real lineage, and the response above carries it.
      //
      // Awaited before navigating: `history.push` unmounts this screen, and the record is what makes the whole
      // applied-vs-adopted measurement mean anything — it is the only signal that a suggestion actually shipped.
      // It swallows its own failures, so a telemetry problem can never read as a failed save.
      await dispatch(recordAdoptionAfterSave(updatedScorecardConfig))

      const nextUrl = scorecardConfig.type === 'copilot' ? '/qa-copilot' : '/coaching/scorecards'
      history.push(`${nextUrl}/${updatedScorecardConfig.id}`)

      if (restoreVersion) {
        toast.success('Scorecard version restored!')
      } else {
        toast.success('Scorecard updated!')
      }
      return updatedScorecardConfig
    } catch (err) {
      if (err?.status === 409) {
        // Refetch the versions so the newer one this toast points to is in the selector.
        dispatch(fetchScorecardConfigVersions(scorecardConfig.sid, scorecardConfig.organization_id))
        toast.error(
          'Another user saved a new version while you were editing. Please note your changes, navigate to the newest version, and try again.',
          { cause: err }
        )
      } else if (err?.name === MISSING_PROMPT_MODEL) {
        toast.error(err.message)
      } else {
        toast.error('Failed to update scorecard', { cause: err })
      }
      return null
    } finally {
      dispatch(setLoading({ scorecardConfig: false }))
    }
  }

export const deleteScorecardConfig = (scorecardConfig, history) => async (dispatch) => {
  try {
    await fetchingAPI(
      `${apiService.scorecard}/scoring/scorecards/scorecard/${scorecardConfig.id}`,
      'DELETE',
      dispatch
    )
    toast.success('Scorecard deleted successfully')
    const nextUrl = scorecardConfig.type === 'copilot' ? '/qa-copilot' : '/coaching/scorecards'
    history.push(nextUrl)
  } catch (err) {
    toast.error('Failed to delete scorecard', { cause: err })
  }
}

export const toggleActivateScorecardConfig = (scorecardConfig) => async (dispatch) => {
  dispatch(setLoading({ scorecardConfig: true }))

  try {
    const updatedScorecardConfig = await fetchingAPI(
      `${apiService.scorecard}/scoring/scorecards/scorecard/${scorecardConfig.sid}/settings?toggle_active=True`,
      'PATCH',
      dispatch
    )

    dispatch(setScorecardConfig(updatedScorecardConfig))
    toast.success(`Scorecard ${scorecardConfig.active ? 'deactivated' : 'activated'} successfully`)
  } catch (err) {
    toast.error(`Failed to ${scorecardConfig.active ? 'deactivate' : 'activate'} scorecard`, {
      cause: err,
    })
  } finally {
    dispatch(setLoading({ scorecardConfig: false }))
  }
}

export const getCopilotTestResult = (testPrompt) => async (dispatch) => {
  dispatch(setLoading({ copilotTestPrompt: true }))

  try {
    const body = JSON.stringify(testPrompt)
    const response = await fetchingAPI(`${apiService.summary}/copilot/test`, 'POST', dispatch, body)

    return response
  } catch (err) {
    return toast.error('Failed to test copilot prompt', { cause: err })
  } finally {
    dispatch(setLoading({ copilotTestPrompt: false }))
  }
}

export const sendEmailToAssignee = (data) => async (dispatch) => {
  try {
    const body = JSON.stringify(data)
    await fetchingAPI(`${apiService.scorecard}/coaching/assign`, 'POST', dispatch, body)
    toast.success('Assigned coaching opportunity')
  } catch (err) {
    toast.error('Failed to assign coaching opportunity', { cause: err })
  }
}

// ==========
// Dashboards
// ==========
export const getCallsScored = (params) => async (dispatch) => {
  dispatch(setLoading({ evaluatedCallData: true }))

  try {
    const evaluatedCallData = await fetchingAPI(
      `${apiService.scorecard}/scoring/scores/clickhouse/aggregate/callCount?${params}`
    )
    dispatch(setData({ evaluatedCallData }))
  } catch (err) {
    toast.error('Failed to get calls scored', { cause: err })
  } finally {
    dispatch(setLoading({ evaluatedCallData: false }))
  }
}
export const getFailedCallCount = (params) => async (dispatch) => {
  dispatch(setLoading({ failedCallData: true }))

  try {
    const failedCallData = await fetchingAPI(
      `${apiService.scorecard}/scoring/scores/clickhouse/aggregate/callCount?${params}&threshold=improvement`,
      'GET',
      dispatch
    )
    dispatch(setData({ failedCallData }))
  } catch (err) {
    toast.error('Failed to get failed call count', { cause: err })
  } finally {
    dispatch(setLoading({ failedCallData: false }))
  }
}

export const getFailedCriteriaCallCount = (params) => async (dispatch) => {
  dispatch(setLoading({ failedCriteriaCallData: true }))

  try {
    const failedCriteriaCallData = await fetchingAPI(
      `${apiService.scorecard}/scoring/scores/clickhouse/aggregate/callCount?${params}&threshold=any_failing_criteria`,
      'GET',
      dispatch
    )
    dispatch(setData({ failedCriteriaCallData }))
  } catch (err) {
    toast.error('Failed to get failed criteria call count', { cause: err })
  } finally {
    dispatch(setLoading({ failedCriteriaCallData: false }))
  }
}

// One fetch populates BOTH `nonCompliantCriteria` (top-10 sorted by
// volume, formatted) and `topInfractors` (raw rows). The legacy
// actions `getNonCompliantCriteria` and `fetchTopInfractors` each
// hit `/aggregate/callCount/by_criteria` with the same params and
// just shaped the same response into different redux keys; this
// combined action fires once and dispatches both keys.
export const fetchCriteriaInfractors = (params) => async (dispatch) => {
  dispatch(setLoading({ nonCompliantCriteria: true, topInfractors: true }))
  try {
    const rows = await fetchingAPI(
      `${apiService.scorecard}/scoring/scores/clickhouse/aggregate/callCount/by_criteria?${params}`,
      'GET',
      dispatch
    )
    const formattedNonCompliantCriteria = rows
      .slice()
      .sort((rowA, rowB) => rowB.call_count - rowA.call_count)
      .slice(0, 10)
      .map((criteria) => ({ ...criteria, name: criteria.criteria_name }))
    dispatch(setData({ nonCompliantCriteria: formattedNonCompliantCriteria, topInfractors: rows }))
  } catch (err) {
    toast.error('Failed to get criteria infractors', { cause: err })
  } finally {
    dispatch(setLoading({ nonCompliantCriteria: false, topInfractors: false }))
  }
}

// Legacy single-key actions kept exported for any non-dashboard
// caller; the dashboard now uses `fetchCriteriaInfractors` instead.
export const getNonCompliantCriteria = (params) => async (dispatch) => {
  dispatch(setLoading({ nonCompliantCriteria: true }))

  try {
    const nonCompliantCriteria = await fetchingAPI(
      `${apiService.scorecard}/scoring/scores/clickhouse/aggregate/callCount/by_criteria?${params}`,
      'GET',
      dispatch
    )
    const formattedNonCompliantCriteria = nonCompliantCriteria
      .sort((a, b) => b.call_count - a.call_count)
      .filter((criteria, i) => i < 10)
      .map((criteria) => ({ ...criteria, name: criteria.criteria_name }))

    dispatch(setData({ nonCompliantCriteria: formattedNonCompliantCriteria }))
  } catch (err) {
    toast.error('Failed to get non-compliant criteria', { cause: err })
  } finally {
    dispatch(setLoading({ nonCompliantCriteria: false }))
  }
}

export const getNonCompliantUsers = (params) => async (dispatch) => {
  dispatch(setLoading({ nonCompliantAgents: true }))

  try {
    const nonCompliantAgents = await fetchingAPI(
      `${apiService.scorecard}/scoring/scores/clickhouse/aggregate/callCount/by_user?${params}&threshold=improvement`,
      'GET',
      dispatch
    )

    const formattedNonCompliantAgents = nonCompliantAgents
      .sort((a, b) => b.call_count - a.call_count)
      .filter((agent, i) => i < 10)
      .map((agent) => ({
        ...agent,
        name: `${agent.first_name} ${agent.last_name}`,
      }))

    dispatch(setData({ nonCompliantAgents: formattedNonCompliantAgents }))
  } catch (err) {
    toast.error('Failed to get non-compliant agents', { cause: err })
  } finally {
    dispatch(setLoading({ nonCompliantAgents: false }))
  }
}

export const fetchAggregateScoresByOrgIdMultipleScorecardsByUser = (params) => async (dispatch) => {
  dispatch(setLoading({ reportingByUser: true }))

  try {
    // No `&threshold=improvement` -- the BE handler explicitly resets
    // filters.threshold to None (analytics.py: scored_calls_by_user) so
    // the param was a misleading no-op. The returned passed/failed
    // counts are over ALL scored calls, not the improvement bucket.
    const scoreData = await fetchingAPI(
      `${apiService.scorecard}/scoring/scores/clickhouse/calls/scored_calls_by_user?${params}`,
      'GET',
      dispatch
    )
    dispatch(setData({ reportingByUser: { aggregate_scores_by_user: scoreData } }))
  } catch (err) {
    dispatch(setFilter('reportingByUser', {}))
    toast.error('Failed to fetch scores', { cause: err })
  } finally {
    dispatch(setLoading({ reportingByUser: false }))
  }
}

export const fetchAggregateScoresByOrgIdMultipleScorecards = (params) => async (dispatch) => {
  dispatch(setLoading({ reporting: true }))

  try {
    const scoreData = await fetchingAPI(
      `${apiService.scorecard}/scoring/scores/clickhouse/aggregate/multiple?${params}`,
      'GET',
      dispatch
    )
    dispatch(setData({ reporting: scoreData }))
  } catch (err) {
    toast.error('Failed to fetch scores', { cause: err })
  } finally {
    dispatch(setLoading({ reporting: false }))
  }
}

export const fetchAggregateScoresByOrgIdMultipleScorecardsByDay = (params) => async (dispatch) => {
  dispatch(setLoading({ reportingByDay: true }))

  try {
    const scoreData = await fetchingAPI(
      `${apiService.scorecard}/scoring/scores/clickhouse/aggregate/multiple/by_date?${params}`,
      'GET',
      dispatch
    )
    dispatch(setData({ reportingByDay: scoreData }))
  } catch (err) {
    toast.error('Failed to fetch scores', { cause: err })
  } finally {
    dispatch(setLoading({ reportingByDay: false }))
  }
}

// Comparative scope tabs need the same cohort dataset across multiple
// widgets (OverviewCardsRow, PeerLeaderboardWidget, and any future
// consumer of useCohortContext). Previously each widget fetched
// independently and the BE 30s cache absorbed the dupes; this thunk
// dedupes at the FE so only one network request fires per
// (scope, filter-bar state).
const COHORT_ENDPOINT_BY_SCOPE = {
  agent: 'calls/scored_calls_by_user',
  scorecard: 'aggregate/multiple',
  tag: 'calls/scored_calls_by_tag',
}

const COHORT_STRIP_KEY = {
  scorecard: 'scorecards',
  agent: 'agents',
  tag: 'tags',
}

// The cohort request strips the scope's own filter dim (you can't
// rank agent X among agents when the data is narrowed to agent X).
// CRITICAL: the dedupe hash below is built from these SAME stripped
// filters -- if the hash and the request ever disagree on inputs, a
// filter change can alter the request without altering the hash and
// the cohort silently never refetches (or vice versa).
const buildCohortRequestFilters = (scope, filters) => {
  const stripKey = COHORT_STRIP_KEY[scope]
  return stripKey ? { ...filters, [stripKey]: null } : filters
}

// Stable hash of the inputs that affect a cohort fetch. Used as both
// the cache key (against `data.cohort.filtersHash`) and the in-flight
// dedup key (against `loading.cohortInFlightHash`). Takes the ALREADY
// STRIPPED request filters so it can't drift from the request.
const buildCohortFiltersHash = (scope, requestFilters, orgId) =>
  JSON.stringify({
    scope,
    orgId,
    startDate: requestFilters?.startDate,
    endDate: requestFilters?.endDate,
    scorecards: requestFilters?.scorecards,
    voipCampaigns: requestFilters?.voipCampaigns,
    agents: requestFilters?.agents,
    tags: requestFilters?.tags,
    dispositions: requestFilters?.dispositions,
    playbooks: requestFilters?.playbooks,
    callType: requestFilters?.callType,
    isWin: requestFilters?.isWin,
    scoredStatus: requestFilters?.scoredStatus,
  })

export const fetchCohortByScope = (scope) => async (dispatch, getState) => {
  if (!scope || !COHORT_ENDPOINT_BY_SCOPE[scope]) {
    return
  }
  const { filters } = getState().qaCopilot
  const { organizationid: currentUserOrgId } = getState().currentUser
  const orgId = filters?.organizationId || currentUserOrgId
  if (!orgId) {
    return
  }

  const requestFilters = buildCohortRequestFilters(scope, filters)
  const filtersHash = buildCohortFiltersHash(scope, requestFilters, orgId)
  const existing = getState().qaCopilot.data.cohort
  if (existing && existing.filtersHash === filtersHash) {
    return
  }
  const inFlight = getState().qaCopilot.loading.cohortInFlightHash
  if (inFlight === filtersHash) {
    return
  }

  dispatch(setLoading({ cohort: true, cohortInFlightHash: filtersHash }))
  try {
    const urlParams = buildDashboardParams(requestFilters, orgId)
    const raw = await fetchingAPI(
      `${apiService.scorecard}/scoring/scores/clickhouse/${COHORT_ENDPOINT_BY_SCOPE[scope]}?${urlParams.toString()}`,
      'GET',
      dispatch
    )
    // Staleness guard: a newer fetch (rapid tab switch) may have
    // superseded this one while we awaited -- dropping the late
    // response beats overwriting the current scope's cohort.
    if (getState().qaCopilot.loading.cohortInFlightHash !== filtersHash) {
      return
    }
    dispatch(setData({ cohort: { scope, filtersHash, raw } }))
  } catch (err) {
    if (getState().qaCopilot.loading.cohortInFlightHash !== filtersHash) {
      return
    }
    // Soft-fail: clear cohort so consumers render empty state rather
    // than stale data from a previous scope.
    dispatch(setData({ cohort: null }))
  } finally {
    // Only clear the loading flags if we're still the current fetch.
    if (getState().qaCopilot.loading.cohortInFlightHash === filtersHash) {
      dispatch(setLoading({ cohort: false, cohortInFlightHash: null }))
    }
  }
}

export const fetchTopInfractors = (params) => async (dispatch) => {
  dispatch(setLoading({ topInfractors: true }))

  try {
    const response = await fetchingAPI(
      `${apiService.scorecard}/scoring/scores/clickhouse/aggregate/callCount/by_criteria?${params}`,
      'GET',
      dispatch
    )
    dispatch(setData({ topInfractors: response }))
  } catch (err) {
    toast.error('Failed to fetch non-compliant agents', { cause: err })
  } finally {
    dispatch(setLoading({ topInfractors: false }))
  }
}

// Derive the immediately-preceding equal-length window from the
// dashboard's current start/end. Mirrors the BE convention
// (prior_end = filters.start_date) used by agents_period_comparison
// and the original `derivePriorWindow` FE helper.
const derivePriorWindowFromParams = (params) => {
  const start = params.get('start_date')
  const end = params.get('end_date')
  if (!start || !end) {
    return null
  }
  const startMs = new Date(start).getTime()
  const endMs = new Date(end).getTime()
  if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs <= startMs) {
    return null
  }
  const span = endMs - startMs
  const priorEnd = startMs
  const priorStart = priorEnd - span
  return {
    priorStart: new Date(priorStart).toISOString(),
    priorEnd: new Date(priorEnd).toISOString(),
  }
}

// Bundled dashboard bootstrap -- one round trip instead of the seven
// previously fired in parallel (callCount × 3 threshold variants,
// aggregate/multiple, aggregate/multiple/by_date for both current AND
// prior windows). Dispatches into the same redux keys the individual
// actions used, so widgets that read
// `state.qaCopilot.data.{evaluatedCallData, failedCallData,
// failedCriteriaCallData, reporting, reportingByDay, reportingByDayPrior}`
// don't need to change. Sections that the BE couldn't compute come back
// as null; the per-key loading flag stays false in that case so widgets
// render a graceful empty state rather than a stuck spinner.
export const fetchCopilotDashboardBootstrap = (params) => async (dispatch) => {
  const KEYS = [
    'evaluatedCallData',
    'failedCallData',
    'failedCriteriaCallData',
    'reporting',
    'reportingByDay',
    'reportingByDayPrior',
  ]
  KEYS.forEach((key) => dispatch(setLoading({ [key]: true })))

  try {
    // Stamp prior-window dates onto the request so the bundle includes
    // the prior by_date rollup. Done client-side because the date
    // arithmetic depends on the active filter window the user picked.
    const url = new URLSearchParams(params)
    const priorWindow = derivePriorWindowFromParams(url)
    if (priorWindow) {
      url.set('prior_start_date', priorWindow.priorStart)
      url.set('prior_end_date', priorWindow.priorEnd)
    }

    const bundle = await fetchingAPI(
      `${apiService.scorecard}/scoring/scores/clickhouse/dashboard/overview/bootstrap?${url.toString()}`,
      'GET',
      dispatch
    )
    if (bundle.evaluated_calls) {
      dispatch(setData({ evaluatedCallData: bundle.evaluated_calls }))
    }
    if (bundle.below_threshold_calls) {
      dispatch(setData({ failedCallData: bundle.below_threshold_calls }))
    }
    if (bundle.failed_criteria_calls) {
      dispatch(setData({ failedCriteriaCallData: bundle.failed_criteria_calls }))
    }
    if (bundle.aggregate_multiple) {
      dispatch(setData({ reporting: bundle.aggregate_multiple }))
    }
    if (bundle.aggregate_multiple_by_date) {
      dispatch(setData({ reportingByDay: bundle.aggregate_multiple_by_date }))
    }
    if (bundle.aggregate_multiple_by_date_prior) {
      dispatch(setData({ reportingByDayPrior: bundle.aggregate_multiple_by_date_prior }))
    }
  } catch (err) {
    toast.error('Failed to load dashboard', { cause: err })
  } finally {
    KEYS.forEach((key) => dispatch(setLoading({ [key]: false })))
  }
}

// Per-load dispatcher used by both the QA Copilot dashboard and any
// flow that re-runs filters. The 5 page-load fetches now ride the
// `/dashboard/overview/bootstrap` bundle (one round trip) instead of
// firing in parallel; the remaining 4 actions still fan out
// individually because they hit unrelated endpoints / are consumed by
// secondary widgets and don't (yet) belong in the bundle.
export const fetchDataFromFilters = (filters) => async (dispatch, getState) => {
  const { organizationid: currentUserOrgId } = getState().currentUser
  const params = formatQACopilotFilters(filters, currentUserOrgId)

  dispatch(fetchCopilotDashboardBootstrap(params))
  dispatch(fetchCriteriaInfractors(params))
  dispatch(getNonCompliantUsers(params))
  dispatch(fetchAggregateScoresByOrgIdMultipleScorecardsByUser(params))
}

export const fetchCopilotDashboardData =
  (organizationId, filtersFromURLParams = {}) =>
  async (dispatch, getState) => {
    const { filters, scopeOverlay } = getState().qaCopilot

    // The active scope tab's picker narrowing rides EVERY dashboard
    // fetch (Apply Filters, org switch, day-click drills...), not just
    // the tab/picker interactions -- otherwise those paths silently
    // revert to org-wide data under a "Comparing X" banner. Explicit
    // per-call overrides still win over the overlay.
    const overlayFilters = scopeOverlay?.filterKey
      ? { [scopeOverlay.filterKey]: scopeOverlay.entity ? [scopeOverlay.entity] : [] }
      : {}

    const updatedFilters = {
      ...filters,
      ...overlayFilters,
      ...filtersFromURLParams,
      callType: filtersFromURLParams.callType || filters.callType,
    }

    const params = formatQACopilotFilters(updatedFilters, organizationId)

    dispatch(
      setData({
        appliedCallType: updatedFilters.callType,
        appliedScorecards: updatedFilters.scorecards || [],
      })
    )
    dispatch(fetchCopilotDashboardBootstrap(params))
    dispatch(fetchCriteriaInfractors(params))
    dispatch(getNonCompliantUsers(params))
    dispatch(fetchAggregateScoresByOrgIdMultipleScorecardsByUser(params))
  }

export const fetchCopilotFilterData = (organizationId) => async (dispatch, getState) => {
  // In-flight guard: the dashboard page and the filter bar both kick
  // this on mount (and org selection kicks it again) -- without the
  // guard the six option fetches fired 2-3x per load.
  if (getState().qaCopilot.loading.filterDataInFlightOrgId === organizationId) {
    return
  }
  dispatch(setLoading({ filterDataInFlightOrgId: organizationId }))
  try {
    await Promise.all([
      dispatch(fetchCopilotScorecardsByOrg(organizationId)),
      dispatch(fetchAgentsByOrg(organizationId)),
      dispatch(fetchTagsByOrg(organizationId)),
      dispatch(fetchPlaybooksByOrg(organizationId)),
      dispatch(fetchVoipCampaignsByOrg(organizationId)),
      dispatch(fetchDispositionsByOrg(organizationId)),
    ])
  } finally {
    if (getState().qaCopilot.loading.filterDataInFlightOrgId === organizationId) {
      dispatch(setLoading({ filterDataInFlightOrgId: null }))
    }
  }
}

export const setSavedFilterById = (filterId, shouldApplyFilters) => async (dispatch, getState) => {
  const { savedFilterList } = getState().savedFilters
  const savedCopilotFilters = savedFilterList[COPILOT_FILTER_TYPE]

  const filterToApply = savedCopilotFilters?.find((filter) => filter.uuid === filterId)

  if (filterToApply?.filters) {
    dispatch(setData({ selectedSavedFilterId: filterId }))

    const { startDate, endDate } = getDateRangeFromSavedFilter(filterToApply.filters.dateRange)

    const cleanedFilters = {
      ...filterToApply.filters,
      organizationId: filterToApply.filters.organization,
      startDate,
      endDate,
      callType: filterToApply.filters.callType,
    }

    cleanedFilters.isWin = cleanBooleanValue(filterToApply.filters.isWin)

    if (cleanedFilters.scorecards && cleanedFilters.scorecards.length > 0) {
      cleanedFilters.scorecards = cleanedFilters.scorecards.map((scorecard) => {
        const sid = scorecard.sid || scorecard.value || scorecard.id
        return {
          id: scorecard.id || sid,
          value: sid,
          label: scorecard.label || scorecard.name || sid,
          sid,
        }
      })
    }

    // Normalize dispositions to nested { selected, includes } format
    if (cleanedFilters.dispositions?.selected) {
      cleanedFilters.dispositions = {
        includes: cleanedFilters.dispositions.includes ?? true,
        selected: cleanedFilters.dispositions.selected.map((val) =>
          typeof val === 'string' ? { value: val, label: val } : val
        ),
      }
    } else {
      cleanedFilters.dispositions = { selected: [], includes: true }
    }

    dispatch(setFilter(cleanedFilters))

    if (cleanedFilters.organizationId) {
      await dispatch(fetchCopilotFilterData(cleanedFilters.organizationId))
    }

    if (shouldApplyFilters) {
      dispatch(fetchCopilotDashboardData())
    }
  }
}

export const clearSavedFilter = () => (dispatch, getState) => {
  dispatch(setData({ selectedSavedFilterId: null }))
  dispatch(clearFilters())

  const { currentUser } = getState()
  const organizationId = currentUser.organizationid

  dispatch(fetchCopilotFilterData(organizationId))
  dispatch(fetchCopilotDashboardData())
}

export const loadDefaultFilters = () => async (dispatch, getState) => {
  try {
    await dispatch(fetchSavedFilters(COPILOT_FILTER_TYPE))
    const { savedFilterList } = getState().savedFilters
    const copilotSavedFilters = savedFilterList[COPILOT_FILTER_TYPE]

    const defaultFilter = copilotSavedFilters.find((filter) => filter.is_default)

    if (defaultFilter) {
      dispatch(setSavedFilterById(defaultFilter.uuid))
    }
  } catch (err) {
    console.error('Error loading default filters:', err)
  }
}

export const fetchInitialCopilotDashboardData =
  (hasOrganizationDropdown, currentUserOrgId, params) => async (dispatch, getState) => {
    const organizationIdFromUrlParams = params?.organization_id
      ? toNumber(params.organization_id)
      : null

    // Get all the things for users that cannot see organizations
    if (!hasOrganizationDropdown) {
      await dispatch(fetchCopilotFilterData(currentUserOrgId))
      dispatch(fetchCopilotDashboardData(currentUserOrgId))
    } else {
      // For Balto Admin and Hierarchy Manager, only do the initial fetch if there are params in the URL
      // eslint-disable-next-line no-lonely-if
      if (!isEmpty(params)) {
        await dispatch(fetchCopilotFilterData(organizationIdFromUrlParams))

        const updatedFiltersFromParams = {
          ...(params.start_date && { startDate: params.start_date }),
          ...(params.end_date && { endDate: params.end_date }),
          ...(params.organization_id && { organizationId: organizationIdFromUrlParams }),
          ...(params.scorecard_config_sid && {
            scorecards: getState().qaCopilot.data.scorecards.filter(
              (scorecard) => scorecard.value === params.scorecard_config_sid
            ),
          }),
        }

        dispatch(setFilter(updatedFiltersFromParams))
        dispatch(fetchCopilotDashboardData(currentUserOrgId, updatedFiltersFromParams))
      }
    }
  }

// =====================
// Enterprise Dashboards
// =====================
export const fetchEnterpriseEvaluatedCalls = (params) => async (dispatch) => {
  dispatch(setLoading({ enterpriseEvaluatedCallData: true }))

  try {
    const response = await fetchingAPI(
      `${apiService.scorecard}/enterprise/aggregate/callCount?${params}`,
      'GET',
      dispatch
    )

    const callIds = response.organizations.flatMap((organization) => organization.call_ids)
    const callData = { call_ids: callIds, call_count: response.total_calls }
    dispatch(setData({ enterpriseEvaluatedCallData: callData }))
  } catch (err) {
    toast.error('Failed to fetch evaluated calls', { cause: err })
  } finally {
    dispatch(setLoading({ enterpriseEvaluatedCallData: false }))
  }
}

export const fetchEnterpriseAverageScore = (params) => async (dispatch) => {
  dispatch(setLoading({ enterpriseAverageScore: true }))

  try {
    const response = await fetchingAPI(
      `${apiService.scorecard}/enterprise/aggregate/multiple?${params}`,
      'GET',
      dispatch
    )
    dispatch(setData({ enterpriseAverageScore: response.aggregate_score }))
  } catch (err) {
    toast.error('Failed to fetch average score', { cause: err })
  } finally {
    dispatch(setLoading({ enterpriseAverageScore: false }))
  }
}

export const fetchEnterpriseFailedCalls = (params) => async (dispatch) => {
  dispatch(setLoading({ enterpriseFailedCallData: true }))

  try {
    const response = await fetchingAPI(
      `${apiService.scorecard}/enterprise/aggregate/callCount?${params}&threshold=improvement`,
      'GET',
      dispatch
    )

    const callIds = response.organizations.flatMap((organization) => organization.call_ids)
    const callData = { call_ids: callIds, call_count: response.total_calls }
    dispatch(setData({ enterpriseFailedCallData: callData }))
  } catch (err) {
    toast.error('Failed to fetch failed calls', { cause: err })
  } finally {
    dispatch(setLoading({ enterpriseFailedCallData: false }))
  }
}

export const fetchEnterpriseScorecardComplianceScores = (params) => async (dispatch) => {
  dispatch(setLoading({ enterpriseScorecardPerformance: true }))

  try {
    const { organizations } = await fetchingAPI(
      `${apiService.scorecard}/enterprise/aggregate/multiple?${params}`,
      'GET',
      dispatch
    )
    dispatch(
      setData({
        enterpriseScorecardPerformance: organizations
          .flatMap(({ organization_name, organization_id, scorecard_configs }) =>
            scorecard_configs.map((config) => ({ ...config, organization_id, organization_name }))
          )
          .map((scorecard) => ({
            ...scorecard,
            organization_id: scorecard.organization_id,
            organization_name: scorecard.organization_name,
          })),
      })
    )
  } catch (err) {
    toast.error('Failed to fetch scorecard performance', { cause: err })
  } finally {
    dispatch(setLoading({ enterpriseScorecardPerformance: false }))
  }
}

export const fetchEnterpriseOrganizationPerformance = (params) => async (dispatch) => {
  dispatch(setLoading({ enterpriseOrganizationPerformance: true }))

  try {
    const [{ organizations }, totalCalls, failingCalls] = await Promise.all([
      fetchingAPI(
        `${apiService.scorecard}/enterprise/aggregate/multiple?${params}`,
        'GET',
        dispatch
      ),
      fetchingAPI(
        `${apiService.scorecard}/enterprise/aggregate/callCount?${params}`,
        'GET',
        dispatch
      ),
      fetchingAPI(
        `${apiService.scorecard}/enterprise/aggregate/callCount?${params}&threshold=improvement`,
        'GET',
        dispatch
      ),
    ])
    const organizationsPerformance = {}
    failingCalls.organizations.forEach(({ organization_name, organization_id, call_ids }) => {
      organizationsPerformance[organization_id] = {
        organization_name,
        organization_id,
        failing_call_ids: call_ids,
      }
    })
    totalCalls.organizations.forEach(({ organization_id, organization_name, call_ids }) => {
      if (!organizationsPerformance[organization_id]) {
        organizationsPerformance[organization_id] = {
          organization_name,
          organization_id,
          failing_call_ids: [],
        }
      } else {
        organizationsPerformance[organization_id].passing_call_ids = call_ids.filter(
          (callId) => !organizationsPerformance[organization_id].failing_call_ids.includes(callId)
        )
      }
    })
    organizations.forEach(({ organization_id, aggregate_score }) => {
      organizationsPerformance[organization_id].aggregate_score = aggregate_score
    })
    dispatch(setData({ enterpriseOrganizationPerformance: organizationsPerformance }))
  } catch (err) {
    toast.error('Failed to fetch organization performance', { cause: err })
  } finally {
    dispatch(setLoading({ enterpriseOrganizationPerformance: false }))
  }
}

export const fetchEnterpriseScoresPerOrganization = (params) => async (dispatch) => {
  dispatch(setLoading({ enterpriseScoresPerOrganization: true }))

  try {
    const response = await fetchingAPI(
      `${apiService.scorecard}/enterprise/aggregate/multiple/by_date?${params}`,
      'GET',
      dispatch
    )
    dispatch(setData({ enterpriseScoresPerOrganization: response }))
  } catch (err) {
    toast.error('Failed to fetch organization performance', { cause: err })
  } finally {
    dispatch(setLoading({ enterpriseScoresPerOrganization: false }))
  }
}

export const fetchEnterpriseDashboardData = (organizationId) => async (dispatch, getState) => {
  const { filters } = getState().qaCopilot

  const filtersWithCallType = {
    ...filters,
    callType: filters.callType,
    scoredStatus: null,
  }

  const params = formatQACopilotFilters(filtersWithCallType, organizationId)

  dispatch(fetchEnterpriseEvaluatedCalls(params))
  dispatch(fetchEnterpriseAverageScore(params))
  dispatch(fetchEnterpriseFailedCalls(params))
  dispatch(fetchEnterpriseScorecardComplianceScores(params))
  dispatch(fetchEnterpriseOrganizationPerformance(params))
  dispatch(fetchEnterpriseScoresPerOrganization(params))
}

export const fetchScorecardPerformance = (scorecardConfigId) => async (dispatch, getState) => {
  dispatch(setLoading({ scorecardPerformance: true }))

  try {
    const { filters } = getState().qaCopilot
    const { organizationid: currentUserOrgId } = getState().currentUser
    const params = formatQACopilotFilters(filters, currentUserOrgId)
    const response = await fetchingAPI(
      `${apiService.scorecard}/scoring/scores/clickhouse/criteria_average/by_sid/${scorecardConfigId}?${params}`,
      'GET',
      dispatch
    )
    dispatch(setData({ scorecardPerformance: response }))
  } catch (err) {
    toast.error('Failed to fetch scorecard performance', { cause: err })
  } finally {
    dispatch(setLoading({ scorecardPerformance: false }))
  }
}

export const fetchCriteriaTrends =
  (scorecardConfigSid, granularity = null) =>
  async (dispatch, getState) => {
    dispatch(setLoading({ criteriaTrends: true }))

    try {
      const { filters } = getState().qaCopilot
      const { organizationid: currentUserOrgId } = getState().currentUser
      const params = formatQACopilotFilters(filters, currentUserOrgId)
      const granularityParam = granularity ? `&granularity=${granularity}` : ''
      const response = await fetchingAPI(
        `${apiService.scorecard}/scoring/scores/clickhouse/criteria_trends/by_sid/${scorecardConfigSid}?${params}${granularityParam}`,
        'GET',
        dispatch
      )
      dispatch(setData({ criteriaTrends: response }))
    } catch (err) {
      toast.error('Failed to fetch criteria trends', { cause: err })
    } finally {
      dispatch(setLoading({ criteriaTrends: false }))
    }
  }

// Bundled per-scorecard overview (TREN-317 perf). Fetches trends +
// correlation matrix + criteria-averages in a single round trip and
// dispatches into the three redux keys the dashboard widgets already
// read (`criteriaTrends`, `criteriaCorrelation`, `scorecardPerformance`).
//
// `scorecardOverviewSid` tracks which sid the populated data belongs to
// so widgets can detect stale data after the picker changes.
export const fetchScorecardOverview =
  (scorecardConfigSid, granularity = null) =>
  async (dispatch, getState) => {
    if (!scorecardConfigSid) {
      return
    }
    const { filters } = getState().qaCopilot
    const currentUserOrgId = getState().currentUser?.organizationid
    const params = formatQACopilotFilters(filters, currentUserOrgId)
    const granularityParam = granularity ? `&granularity=${granularity}` : ''
    const url = `${apiService.scorecard}/scoring/scores/clickhouse/scorecard/${scorecardConfigSid}/overview?${params}${granularityParam}`

    // Dedupe + staleness guard (same discipline as fetchCohortByScope):
    // CriteriaTrendsWidget and ScorecardPerformanceTable are co-mounted
    // and both dispatch this on every picker/filter change -- without
    // the in-flight key the heaviest dashboard query (it includes the
    // correlation matrix) fired twice per interaction, and rapid picker
    // changes could land scorecard A's data under scorecard B's title.
    const inFlight = getState().qaCopilot.loading.scorecardOverviewInFlightUrl
    if (inFlight === url) {
      return
    }
    dispatch(
      setLoading({
        scorecardOverview: true,
        criteriaTrends: true,
        criteriaCorrelation: true,
        scorecardPerformance: true,
        scorecardOverviewInFlightUrl: url,
      })
    )

    const isCurrent = () => getState().qaCopilot.loading.scorecardOverviewInFlightUrl === url

    try {
      const bundle = await fetchingAPI(url, 'GET', dispatch)
      // A newer fetch superseded this one -- drop the late response.
      if (!isCurrent()) {
        return
      }
      dispatch(
        setData({
          scorecardOverviewSid: scorecardConfigSid,
          criteriaTrends: bundle.trends || null,
          criteriaCorrelation: bundle.correlation || null,
          scorecardPerformance: bundle.criteria_averages || {},
        })
      )
    } catch (err) {
      if (!isCurrent()) {
        return
      }
      toast.error('Failed to fetch scorecard overview', { cause: err })
    } finally {
      if (isCurrent()) {
        dispatch(
          setLoading({
            scorecardOverview: false,
            criteriaTrends: false,
            criteriaCorrelation: false,
            scorecardPerformance: false,
            scorecardOverviewInFlightUrl: null,
          })
        )
      }
    }
  }
